import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		const initAuth = async () => {
			try {
				// Check if token exists in localStorage
				const token = localStorage.getItem("token");

				if (token) {
					// Set auth header for all requests
					axios.defaults.headers.common["x-auth-token"] = token;

					// Fetch user data
					const res = await axios.get("/api/auth/user");

					// Fetch user permissions
					const permRes = await axios.get("/api/auth/permissions");

					setUser({
						...res.data.user,
						permissions: permRes.data.permissions || [],
					});
				}
			} catch (err) {
				console.error("Auth initialization error:", err);
				// Clear token if invalid
				localStorage.removeItem("token");
				delete axios.defaults.headers.common["x-auth-token"];
			} finally {
				setLoading(false);
			}
		};

		initAuth();
	}, []);

	const login = async (username, password) => {
		try {
			setError(null);

			const res = await axios.post("/api/auth/login", { username, password });

			const { token, user } = res.data;

			// Save token to localStorage
			localStorage.setItem("token", token);

			// Set auth header for future requests
			axios.defaults.headers.common["x-auth-token"] = token;

			setUser(user);
			return user;
		} catch (err) {
			setError(err.response?.data?.message || "Login failed");
			throw err;
		}
	};

	const register = async (username, email, password) => {
		try {
			setError(null);

			const res = await axios.post("/api/auth/register", {
				username,
				email,
				password,
			});

			const { message, user } = res.data;

			// Do not log the user in immediately since they are pending approval
			return { message, user };
		} catch (err) {
			setError(err.response?.data?.message || "Registration failed");
			throw err;
		}
	};

	const logout = () => {
		// Remove token from localStorage
		localStorage.removeItem("token");

		// Remove auth header
		delete axios.defaults.headers.common["x-auth-token"];

		setUser(null);
	};

	const isAdmin = () => {
		return user?.role === "admin";
	};

	return <AuthContext.Provider value={{ user, loading, error, login, register, logout, isAdmin }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	const context = useContext(AuthContext);

	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}

	return context;
};

export default useAuth;
